/**
 * Formatage FR partagé par les deux fronts.
 *
 * Les dates sont affichées dans le fuseau du navigateur, pas dans celui du
 * tenant : l'utilisateur qui consulte le dashboard est physiquement sur site,
 * et une heure « traduite » dans un autre fuseau serait trompeuse lors d'une
 * levée de doute.
 */

const DATE_TIME = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const DATE_ONLY = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const TIME_ONLY = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' });

export const formatDateTime = (iso: string | null | undefined): string =>
  iso ? DATE_TIME.format(new Date(iso)) : '—';

export const formatDate = (iso: string | null | undefined): string =>
  iso ? DATE_ONLY.format(new Date(iso)) : '—';

export const formatTime = (iso: string | null | undefined): string =>
  iso ? TIME_ONLY.format(new Date(iso)) : '—';

/** Écart relatif court, du type « il y a 2 min ». */
export function formatRelative(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '—';

  const deltaSeconds = Math.round((now.getTime() - new Date(iso).getTime()) / 1000);
  if (deltaSeconds < 60) return "à l'instant";

  // La minute est l'unité plancher : les écarts inférieurs sont déjà traités
  // au-dessus. Elle est isolée du tableau pour servir de valeur initiale sans
  // indexation, que `noUncheckedIndexedAccess` rendrait potentiellement nulle.
  const smallest: [Intl.RelativeTimeFormatUnit, number] = ['minute', 60];
  const larger: readonly [Intl.RelativeTimeFormatUnit, number][] = [
    ['hour', 3_600],
    ['day', 86_400],
    ['month', 2_592_000],
    ['year', 31_536_000],
  ];

  let chosen = smallest;
  for (const unit of larger) {
    if (Math.abs(deltaSeconds) >= unit[1]) chosen = unit;
  }

  const formatter = new Intl.RelativeTimeFormat('fr-FR', { numeric: 'auto', style: 'short' });
  return formatter.format(-Math.round(deltaSeconds / chosen[1]), chosen[0]);
}

/** Durée en minutes → « 1 h 45 », pour le MTTR. */
export function formatMinutes(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return '—';
  if (minutes < 60) return `${Math.round(minutes)} min`;

  const hours = Math.floor(minutes / 60);
  const rest = Math.round(minutes % 60);
  if (hours < 24) return rest === 0 ? `${hours} h` : `${hours} h ${rest}`;

  const days = Math.floor(hours / 24);
  return `${days} j ${hours % 24} h`;
}

export const formatPercent = (ratio: number, digits = 1): string =>
  `${(ratio * 100).toFixed(digits)} %`;

export const formatNumber = (value: number): string => new Intl.NumberFormat('fr-FR').format(value);
